import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { fmtDateTime } from '../utils/formatters';
import { AlertTriangle, Trash2 } from 'lucide-react';

export default function UserManagementPage({ showToast }) {
  const { request, loading } = useApi();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const data = await request('/api/admin/users');
      setUsers(data || []);
    } catch (err) {
      showToast(err.message, 'error');
    }
  }, [request, showToast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await request(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      showToast('User role updated', 'success');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  const handleDeleteUser = async (userId, username) => {
    if (deletingId !== userId) {
      setDeletingId(userId);
      setTimeout(() => setDeletingId(null), 5000); // Reset after 5s
      return;
    }

    try {
      await request(`/api/admin/users/${userId}`, { method: 'DELETE' });
      showToast('User removed successfully', 'success');
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeletingId(null);
    } catch (err) {
      showToast(err.message, 'error');
      setDeletingId(null);
    }
  };

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(search.toLowerCase()) || 
    (u.email && u.email.toLowerCase().includes(search.toLowerCase())) ||
    (u.name && u.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="user-management-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
           <h1 className="page-title" style={{ margin: 0 }}>User Management</h1>
           <p className="page-subtitle" style={{ margin: '4px 0 0 0' }}>Manage platform users, roles, and administrative access.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
           <button onClick={() => navigate('/admin')} className="nav-icon-btn" title="Go Back">←</button>
        </div>
      </div>

      <div className="card mt-6" style={{ background: 'var(--dark-panel)', border: '1px solid var(--white-05)', borderRadius: 16, overflow: 'hidden', backdropFilter: 'blur(16px)' }}>
        <div className="p-6 border-bottom flex justify-between items-center" style={{ background: 'var(--white-02)', borderBottom: '1px solid var(--white-05)' }}>
           <input 
             placeholder="Search by name, email or username..." 
             className="input" 
             style={{ maxWidth: '400px', height: 42, background: 'var(--black-30)', border: '1px solid var(--white-06)', borderRadius: 10, padding: '0 16px', color: 'var(--text-primary)', outline: 'none' }}
             value={search}
             onChange={e => setSearch(e.target.value)}
           />
           <div className="text-sm font-bold" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Users: {users.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="ticket-table">
            <thead>
              <tr>
                <th>Name / Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Auth Type</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--white-04)', height: 64 }}>
                  <td style={{ padding: '0 24px' }}>
                    <div className="font-bold" style={{ color: 'var(--text-primary)', fontSize: 15 }}>
                      {u.is_internal ? 'System Local Administrator' : (u.name || u.username)}
                      {u.is_internal && (
                        <span style={{ 
                          marginLeft: '8px', padding: '2px 6px', borderRadius: 4, background: 'rgba(255, 184, 0, 0.1)', 
                          color: '#FFB800', fontSize: 9, fontWeight: 900, verticalAlign: 'middle', border: '1px solid rgba(255, 184, 0, 0.2)',
                          letterSpacing: '0.05em'
                        }}>
                          PROTECTED
                        </span>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>@{u.username}</div>
                  </td>
                  <td style={{ padding: '0 24px', color: 'var(--text-secondary)' }}>{u.email || <span className="text-muted">—</span>}</td>
                  <td style={{ padding: '0 24px' }}>
                    <span style={{ 
                      padding: '4px 12px', borderRadius: 20, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
                      background: u.role === 'admin' ? 'rgba(248, 113, 113, 0.1)' : u.role === 'it' ? 'rgba(96, 165, 250, 0.1)' : 'var(--white-05)',
                      color: u.role === 'admin' ? '#F87171' : u.role === 'it' ? 'var(--text-60a5fa)' : 'var(--white-50)',
                      border: `1px solid ${u.role === 'admin' ? 'rgba(248, 113, 113, 0.2)' : u.role === 'it' ? 'rgba(96, 165, 250, 0.2)' : 'var(--white-06)'}`
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '0 24px' }}>
                    <span style={{ 
                      padding: '4px 12px', borderRadius: 6, fontSize: 10, fontWeight: 800, color: u.ldap_user ? 'var(--text-3b82f6)' : '#6366F1',
                      background: u.ldap_user ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                      border: `1px solid ${u.ldap_user ? 'rgba(16, 185, 129, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`
                    }}>
                      {u.ldap_user ? 'LDAP' : 'LOCAL'}
                    </span>
                  </td>
                  <td className="text-xs" style={{ padding: '0 24px', color: 'var(--text-muted)' }}>{fmtDateTime(u.created_at)}</td>
                  <td style={{ padding: '0 24px' }}>
                    <div className="flex items-center gap-3">
                      <select 
                        className="input input-sm" 
                        style={{ width: '130px', height: 32, padding: '0 8px', borderRadius: 8, background: '#0b1f2a', border: '1px solid rgba(96, 165, 250, 0.2)', color: '#fff', fontSize: 12, cursor: u.is_internal ? 'not-allowed' : 'pointer', outline: 'none', opacity: u.is_internal ? 0.5 : 1 }}
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        disabled={u.is_internal}
                      >
                        <option value="employee" style={{ background: '#0b1f2a', color: '#fff' }}>Employee</option>
                        <option value="it" style={{ background: '#0b1f2a', color: '#fff' }}>IT Support</option>
                        <option value="admin" style={{ background: '#0b1f2a', color: '#fff' }}>Admin</option>
                      </select>
                      <button 
                        onClick={() => handleDeleteUser(u.id, u.username)}
                        disabled={u.is_internal}
                        style={{ 
                          height: 32, padding: '0 12px', borderRadius: 8, border: 'none',
                          background: deletingId === u.id ? '#DC2626' : 'rgba(220, 38, 38, 0.1)',
                          color: deletingId === u.id ? '#fff' : '#DC2626',
                          fontSize: 12, fontWeight: 700, cursor: u.is_internal ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                          display: 'flex', alignItems: 'center', gap: 6,
                          opacity: u.is_internal ? 0.3 : 1
                        }}
                      >
                        {deletingId === u.id ? <><AlertTriangle size={14} /> Confirm?</> : <><Trash2 size={14} /> Delete</>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-muted">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
